<template>
  <div>
    <LoadingState
      v-if="loading"
      :loading="loading"
    />
    <v-container
      grid-list-xl
      fluid
    >
      <v-layout
        row
        wrap
        color="transparent"
        class="mb-3"
      >
        <!-- Toolbar Navigation -->
        <v-toolbar
          flat
          color="transparent"
          class="mb-4 mt-4"
          height="20px"
        >
          <v-btn
            icon
            class="text--secondary"
            @click="goBack"
          >
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <span class="text-capitalize subheading text--secondary">
            {{ $t('Settings') }}
          </span>
        </v-toolbar>

        <!-- Title -->
        <v-flex xs12>
          <h3 class="text-capitalize ml-2 font-weight-regular">
            {{ $t('General Settings') }}
          </h3>
          <v-divider mb-3/>
        </v-flex>
      </v-layout>

      <v-layout
        row
        wrap
      >
        <v-flex xs12>
          <v-expansion-panel>
            <v-expansion-panel-content v-if="storeAddress">
              <template v-slot:header>
                <div>
                  <div>
                    <h3 class="title font-weight-regular pt-3">
                      {{ $t('Contact Address') }}
                    </h3>
                  </div>
                  <div class="pt-2">
                    <div>{{ $t('Store address used for mailing, store footer and other settings.') }}</div>
                  </div>
                </div>
              </template>

              <v-divider/>

              <v-layout
                align-center
                justify-center
                row
              >
                <v-flex
                  lg6
                  md8
                  class="pa-4"
                >
                  <v-text-field
                    v-model="storeAddress.name"
                    :error-messages="storeAddress.displayErrors('name')"
                    :label="$t('Name')"/>
                  <v-text-field
                    v-model="storeAddress.street"
                    :error-messages="storeAddress.displayErrors('street')"
                    :label="$t('Street')"/>
                  <country-select
                    v-model="storeAddress.country_code"
                    :error-messages="storeAddress.displayErrors('country_code')"
                    :label="$t('Country')"/>
                  <region-select
                    :country="storeAddress.country_code"
                    v-model="storeAddress.region"
                    :error-messages="storeAddress.displayErrors('region')"
                    :label="$t('Region')"/>
                  <v-text-field
                    v-model="storeAddress.city"
                    :error-messages="storeAddress.displayErrors('city')"
                    :label="$t('City')"/>
                  <v-text-field
                    v-model="storeAddress.postcode"
                    :error-messages="storeAddress.displayErrors('postcode')"
                    :label="$t('Postcode')"/>

                  <template v-if="generalSettings">
                    <v-divider class="my-4 pb-2"/>

                    <v-text-field
                      v-model="generalSettings.contact.email"
                      :label="$t('Email')"/>
                    <v-text-field
                      v-model="generalSettings.contact.telephone"
                      :label="$t('Telephone')"/>
                  </template>
                </v-flex>
              </v-layout>
            </v-expansion-panel-content>

            <v-expansion-panel-content v-if="generalSettings">
              <template v-slot:header>
                <div>
                  <div>
                    <h3 class="title font-weight-regular pt-3">
                      {{ $t('Currency') }}
                    </h3>
                  </div>
                  <div class="pt-2">
                    <div>{{ $t('Store default currency. Default currency is used with every resource that has price.') }}</div>
                  </div>
                </div>
              </template>

              <v-divider/>

              <v-layout
                align-center
                justify-center
                row
              >
                <v-flex
                  lg6
                  md8
                  class="pa-4"
                >
                  <v-combobox
                    v-model="generalSettings.currency.default"
                    :items="currencies"
                    :label="$t('Store Currency')"
                  />
                </v-flex>
              </v-layout>
            </v-expansion-panel-content>

            <v-expansion-panel-content v-if="generalSettings">
              <template v-slot:header>
                <div>
                  <div>
                    <h3 class="title font-weight-regular pt-3">
                      {{ $t('Locale and Timezone') }}
                    </h3>
                  </div>
                  <div class="pt-2">
                    <span>{{ $t('Store default locale and timezone with configuration for time format.') }}</span>
                    <span> {{ $t('Locale is being used for dates and currency formatting.') }}</span>
                  </div>
                </div>
              </template>

              <v-divider/>

              <v-layout
                align-center
                justify-center
                row
              >
                <v-flex
                  lg6
                  md8
                  class="pa-4"
                >
                  <v-layout
                    row
                    wrap
                  >
                    <v-flex lg12>
                      <h3>{{ $t('Locale') }} </h3>

                      <v-text-field
                        v-model.trim="generalSettings.locale.default"
                        :error-messages="localeValidation"
                        :label="$t('Store Locale')"
                      />
                    </v-flex>

                    <v-flex lg12>
                      <h3 class="mb-2">
                        {{ $t('Date Format') }}
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="pointer"
                              color="secondary"
                              dark
                              v-on="on">
                              info
                            </v-icon>
                          </template>
                          <span>{{ $t('The Global field is used as default date format. The Console, PoS and Backend fields, when set, will take precedence over Global in the respective apps.') }}</span>
                        </v-tooltip>
                      </h3>

                      <date-format-setting
                        v-model="generalSettings.date_format.global"
                        :locale="generalSettings.locale.default"
                        title="Global"
                      />

                      <date-format-setting
                        v-model="generalSettings.date_format.console"
                        :locale="generalSettings.locale.default"
                        title="Console"
                      />

                      <date-format-setting
                        v-model="generalSettings.date_format.pos"
                        :locale="generalSettings.locale.default"
                        title="PoS"
                      />

                      <date-format-setting
                        v-model="generalSettings.date_format.backend"
                        :locale="generalSettings.locale.default"
                        title="Backend"
                        description="Backend stands for documents and other Application related parts with dates."
                      />
                    </v-flex>

                    <v-flex lg12>
                      <h3>
                        {{ $t('Timezone') }}
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="pointer"
                              color="secondary"
                              dark
                              v-on="on">
                              info
                            </v-icon>
                          </template>
                          <span>{{ $t('The Global field is used as default timezone. The Console, PoS and Backend fields, when set, will take precedence over Global in the respective apps.') }}</span>
                        </v-tooltip>
                      </h3>

                      <v-combobox
                        v-model.trim="generalSettings.timezone.global"
                        :items="timezonesAbbreviations"
                        :error-messages="localeValidation"
                        :label="$t('Global')"
                      />

                      <v-combobox
                        v-model.trim="generalSettings.timezone.console"
                        :items="timezonesAbbreviations"
                        :error-messages="localeValidation"
                        :label="$t('Console')"
                      />

                      <v-combobox
                        v-model.trim="generalSettings.timezone.pos"
                        :items="timezonesAbbreviations"
                        :error-messages="localeValidation"
                        :label="$t('PoS')"
                      />

                      <v-combobox
                        v-model.trim="generalSettings.timezone.backend"
                        :items="timezonesAbbreviations"
                        :error-messages="localeValidation"
                        :label="$t('Backend')"
                      />
                      <span class="font-italic">{{ $t('Backend stands for documents and other Application related parts with dates.') }}</span>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-expansion-panel-content>

            <v-expansion-panel-content v-if="generalSettings">
              <template v-slot:header>
                <div>
                  <div>
                    <h3 class="title font-weight-regular pt-3">
                      {{ $t('Brand') }}
                    </h3>
                  </div>
                  <div class="pt-2">
                    <div>{{ $t('Store Brand settings, like logo.') }}</div>
                  </div>
                </div>
              </template>

              <v-divider/>

              <v-layout
                align-center
                justify-center
                row
              >
                <v-flex
                  lg6
                  md8
                  class="pa-4"
                >
                  <image-upload v-model="generalSettings.brand.logo"/>
                </v-flex>
              </v-layout>
            </v-expansion-panel-content>

            <v-expansion-panel-content v-if="generalSettings">
              <template v-slot:header>
                <div>
                  <div>
                    <h3 class="title font-weight-regular pt-3">
                      Cloudinary
                    </h3>
                  </div>
                  <div class="pt-2">
                    <div>{{ $t('Store Cloudinary credentials (name and preset).') }}</div>
                  </div>
                </div>
              </template>

              <v-divider/>

              <v-layout
                align-center
                justify-center
                row
              >
                <v-flex
                  lg6
                  md8
                  class="pa-4"
                >
                  <v-text-field
                    v-model.trim="generalSettings.cloudinary.name"
                    :label="$t('Cloud Name')"
                  />
                  <v-text-field
                    v-model.trim="generalSettings.cloudinary.preset"
                    :label="$t('Upload Preset')"
                  />
                </v-flex>
              </v-layout>
            </v-expansion-panel-content>

          </v-expansion-panel>
        </v-flex>
      </v-layout>
    </v-container>
    <v-layout
      row
      wrap
      color="transparent"
    >
      <v-flex
        xs12
        ma-3
      >
        <v-divider mb-3/>
        <v-btn
          large
          class="primary right mt-3"
          @click="save()"
        >
          {{ $t('Save') }}
        </v-btn>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import LoadingState from '@/components/LoadingState'
import CountrySelect from '@/components/widgets/CountrySelect'
import RegionSelect from '@/components/widgets/RegionSelect'
import currencies from 'js-money/lib/currency'
import ImageUpload from '@/components/ImageUpload'
import { mapActions } from 'vuex'
import DateFormatSetting from '@/modules/Settings/components/General/DateFormatSetting'
import { timezonesAbbreviations } from '@/modules/Settings/timezones'

export default {
  name: 'SettingsGeneral',

  components: {
    DateFormatSetting,
    ImageUpload,
    RegionSelect,
    CountrySelect,
    LoadingState
  },

  data () {
    return {
      loading: false,
      progress: false,
      storeAddress: null,
      generalSettings: null,
      currencies: [],
      logoImage: '',
      timezonesAbbreviations: timezonesAbbreviations
    }
  },

  computed: {
    localeValidation () {
      if (!this.generalSettings.locale.default) {
        return ''
      }

      const valid = this.generalSettings.locale.default.match('^[a-z]{2}-[A-Z]{2}$')
      return valid ? '' : this.$t('Invalid locale format. Valid format: `xx-XX`')
    }
  },

  async mounted () {
    this.currencies = Object.keys(currencies)

    this.storeAddress = await this.$http.make('contact_address').find('contact/address')
    this.generalSettings = await this.$http.make('settings').find('general')
  },

  methods: {
    ...mapActions([
      'setLanguagesList', 'setDefaultLang', 'setTimezone', 'setDateFormat'
    ]),
    goBack () {
      this.$router.push({ name: 'settings' })
    },

    async save () {
      await this.saveGeneral()
      this.$toast(this.$t('Settings saved'))
    },

    async saveGeneral () {
      if (this.generalSettings.contact.telephone) {
        this.generalSettings.contact.telephone = this.generalSettings.contact.telephone.toString() // make sure its a string
      }

      if (this.logoImage) {
        this.generalSettings.brand.logo = this.logoImage
        this.logoImage = ''
      }

      let settings = this.$http.make('settings', {
        data: {
          contact: {
            address: {
              name: this.storeAddress.name,
              street: this.storeAddress.street,
              city: this.storeAddress.city,
              region: this.storeAddress.region,
              postcode: this.storeAddress.postcode,
              country_code: this.storeAddress.country_code
            },
            email: this.generalSettings.contact.email,
            telephone: this.generalSettings.contact.telephone
          },
          brand: this.generalSettings.brand,
          currency: this.generalSettings.currency,
          locale: this.generalSettings.locale,
          cloudinary: this.generalSettings.cloudinary,
          timezone: this.generalSettings.timezone,
          checkout: this.generalSettings.checkout,
          date_format: this.getDateFormat()
        }
      })

      settings.code_url = 'general'

      try {
        await settings.save()

        this.setupTimezones()
      } catch (e) {
        this.$toast.error(this.$t('General Settings:') + e.message)
      }
    },

    setupTimezones () {
      this.setTimezone({
        level: 'global',
        data: this.generalSettings.timezone.global
      })
      this.setTimezone({
        level: 'console',
        data: this.generalSettings.timezone.console
      })
      this.setDateFormat({
        level: 'global',
        data: this.generalSettings.date_format.global
      })
      this.setDateFormat({
        level: 'console',
        data: this.generalSettings.date_format.console
      })
    },

    getDateFormat () {
      return {
        global: this.generalSettings.date_format.global,
        console: this.dateFormat('console'),
        pos: this.dateFormat('pos'),
        backend: this.dateFormat('backend')
      }
    },

    dateFormat (level) {
      const format = this.generalSettings.date_format[level]
      if (!format ||
        (typeof format === 'object' && (!Object.values(format).length || Object.values(format).every(v => !v)))) {
        return null
      }

      return format
    },

    update (file) {
      this.progress = true
      this.upload(file)
        .then(async res => {
          const response = res.data
          this.logoImage = ''
          this.generalSettings.brand.logo = response.secure_url
        })
        .catch(error => {
          this.$toast.error(this.$t(error.message))
        })
    },

    async upload (file) {
      const cloudinary = await this.$http.make('general_settings').getCloudinary()
      const url = `https://api.cloudinary.com/v1_1/${cloudinary.name}/upload`

      const fd = new FormData()
      fd.append('upload_preset', cloudinary.preset)
      fd.append('tags', 'browser_upload') // Optional - add tag for image admin in Cloudinary
      fd.append('file', file)

      return require('axios').post(url, fd)
    }
  }
}
</script>

<style scoped>
.logo-position {
  margin-top: -15px
}
</style>
